import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import InputField from '~/components/forms/InputField';
import Button from '~/components/ui/Button';
import { CardBox } from '~/components/ui/Card';
import Icon from '~/components/ui/Icon';
import useDeleteUserAccount from '~/modules/user/useDeleteUserAccount';

const WHAT_HAPPENS_ITEMS = ['dataRemoval', 'accountRemoval', 'subscriptions', 'recovery'] as const;

const SECTION_HEADING =
  'mb-6 text-[21px] font-semibold leading-[1.4] tracking-[-0.3px] compact:text-lg compact:mb-4';

function AccountDeletion() {
  const { t } = useTranslation('', { keyPrefix: 'accountDeletion' });
  const { mutateAsync: deleteAccount } = useDeleteUserAccount();
  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm({
    defaultValues: { confirmed: false, deleted: '' },
    mode: 'onTouched',
    resolver: joiResolver(
      Joi.object({
        confirmed: Joi.boolean().valid(true),
        deleted: Joi.string()
          .valid('DELETE')
          .messages({
            'any.only': t('confirmation.validation.deleteText'),
          }),
      }),
    ),
  });

  return (
    <div>
      <Link
        href="/data-management"
        className="mb-6 inline-flex items-center gap-2 text-[13px] text-gray-3 transition-colors hover:text-accent compact:hidden"
      >
        <Icon name="arrowBack" className="text-[18px]" />
        Back
      </Link>

      <h2 className="text-[32px] leading-[1.2] tracking-[-0.8px] font-semibold compact:text-[28px]">
        {t('title')}
      </h2>

      <p className="mt-3 max-w-180 text-[14px] phone:text-[15px] leading-[1.8] text-body compact:hidden">
        {t('intro')}
      </p>

      <div className="mt-10 flex flex-col gap-6 compact:mt-5 compact:gap-5">
        <CardBox className="p-8 compact:p-4">
          <h3 className={SECTION_HEADING}>{t('whatHappens.title')}</h3>
          <dl className="m-0 grid gap-5 text-[14px] leading-[1.8] compact:gap-3.5 compact:leading-[1.6]">
            {WHAT_HAPPENS_ITEMS.map((key) => (
              <div key={key}>
                <dt className="font-semibold text-consequence-title">
                  {t(`whatHappens.items.${key}.title`)}:
                </dt>
                <dd className="m-0 mt-1 text-body">{t(`whatHappens.items.${key}.description`)}</dd>
              </div>
            ))}
          </dl>
        </CardBox>

        <section className="rounded-2xl border border-warning-border bg-warning-background p-8 compact:p-4">
          <h3 className={`${SECTION_HEADING} text-destruction`}>{t('warning.title')}</h3>
          <ul className="m-0 list-disc pl-5 text-[14px] leading-[1.9] text-body compact:leading-[1.6]">
            {(t('warning.items', { returnObjects: true }) as string[]).map(
              (item: string, index: number) => (
                <li key={index}>{item}</li>
              ),
            )}
          </ul>
        </section>

        <CardBox className="p-8 compact:p-4">
          <h3 className={SECTION_HEADING}>{t('beforeProceed.title')}</h3>
          <dl className="m-0 grid gap-5 text-[14px] leading-[1.8] compact:gap-3.5 compact:leading-[1.6]">
            <div>
              <dt className="font-semibold text-consequence-title">
                {t('beforeProceed.items.cancelSubscriptions.title')}:
              </dt>
              <dd className="m-0 mt-1 text-body">
                {t('beforeProceed.items.cancelSubscriptions.description')}
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-consequence-title">
                {t('beforeProceed.items.downloadData.title')}:
              </dt>
              <dd className="m-0 mt-1 text-body">
                <Trans
                  t={t}
                  i18nKey="beforeProceed.items.downloadData.description"
                  components={[
                    <Link
                      key="data-management"
                      href="/data-management"
                      className="text-link underline underline-offset-[3px]"
                    />,
                  ]}
                />
              </dd>
            </div>
          </dl>
        </CardBox>

        <CardBox className="p-8 compact:p-4">
          <form onSubmit={handleSubmit(() => deleteAccount())}>
            <h3 className={SECTION_HEADING}>{t('confirmation.title')}</h3>
            <InputField
              control={control}
              name="deleted"
              label={t('confirmation.instruction')}
              placeholder={t('confirmation.placeholder')}
              autoComplete="off"
            />
            <label className="my-6 flex cursor-pointer items-start gap-3 text-[13px] leading-[1.8] text-body">
              <input
                {...register('confirmed')}
                type="checkbox"
                disabled={isSubmitting}
                className="m-0 mt-1 size-4 shrink-0 cursor-pointer accent-radio-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-outline disabled:cursor-default"
              />
              <span>{t('confirmation.checkbox')}</span>
            </label>
            <div className="flex items-center justify-end gap-6 compact:gap-3">
              <Button
                type="submit"
                variant="danger"
                isPending={isSubmitting}
                isDisabled={!isValid}
                className="compact:ml-auto"
              >
                {t('confirmation.submit')}
              </Button>
            </div>
          </form>
        </CardBox>
      </div>
    </div>
  );
}

export default AccountDeletion;
